import type { DatabaseEntryFieldValuesSnapshot } from '@databases/ai-first-creation-serializer/database-entry-field-values-snapshot';
import type {
  AiFieldGenerationWriteContext,
  AiFieldStreamTarget
} from '@databases/ai-first-creation-token-processing';
import {
  fetchDatabaseAiTeammate,
  iterateDatabaseAiTeammateDocumentParts,
  type DatabaseAiTeammateTraceContext
} from '@databases/ai-teammate-client';
import { collabStateManager } from '@databases/collab-state-manager';
import { logError } from '@databases/logs';

import { DatabaseStreamState } from './database-stream-state.ts';
import { readDatabaseContentBatches } from './handlers/read-database-content-handler.ts';

type RunDatabaseAiFieldBaseOptions = {
  contentId: string;
  locale: string;
  prompt: string;
  traceContext: DatabaseAiTeammateTraceContext;
  abortSignal?: AbortSignal;
  entryFieldValuesSnapshot?: DatabaseEntryFieldValuesSnapshot;
};

type FieldAiTarget = Extract<AiFieldStreamTarget, { scope: 'field' }>;
type EntryAiTarget = Extract<AiFieldStreamTarget, { scope: 'entry' }>;
type EntriesAiTarget = Extract<AiFieldStreamTarget, { scope: 'entries' }>;
type ExplicitEntryContextScope = {
  kind: 'entry-ids';
  entryIds: readonly string[];
};

const DATABASE_AI_FIELD_BATCH_SIZE = 25;

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError';

export type RunDatabaseAiFieldOptions = RunDatabaseAiFieldBaseOptions &
  (
    | {
        target: FieldAiTarget;
        contextScope:
          | { kind: 'all-entries' }
          | { kind: 'empty-target-field-entries' }
          | ExplicitEntryContextScope;
        generationWriteContext?: AiFieldGenerationWriteContext;
      }
    | {
        target: EntryAiTarget;
        contextScope?: never;
        generationWriteContext?: AiFieldGenerationWriteContext;
      }
    | {
        target: EntriesAiTarget;
        contextScope?: never;
        generationWriteContext?: AiFieldGenerationWriteContext;
      }
  );

function buildDatabaseEntryScope(options: RunDatabaseAiFieldOptions) {
  const contextScope = options.contextScope;
  if (contextScope === undefined) {
    switch (options.target.scope) {
      case 'entry':
        return {
          kind: 'specific-entries',
          entryIds: [options.target.entryId]
        } as const;
      case 'entries':
        return {
          kind: 'specific-entries',
          entryIds: options.target.entryIds
        } as const;
      default: {
        const unhandledTarget: never = options.target;
        throw new Error(`Unhandled AI field target: ${String(unhandledTarget)}`);
      }
    }
  }

  switch (contextScope.kind) {
    case 'all-entries':
      return { kind: 'all-entries' } as const;
    case 'empty-target-field-entries':
      return {
        kind: 'empty-ai-summary-entries',
        fieldId: options.target.fieldId
      } as const;
    case 'entry-ids':
      return {
        kind: 'specific-entries',
        entryIds: contextScope.entryIds
      } as const;
    default: {
      const unhandledScope: never = contextScope;
      throw new Error(`Unhandled AI field context scope: ${String(unhandledScope)}`);
    }
  }
}

function assertTargetExists(contentId: string, target: AiFieldStreamTarget): void {
  const database = collabStateManager.getCollabManager(contentId)?.database;
  if (!database) {
    throw new Error('Database not found');
  }

  if (!database.fieldsById[target.fieldId]) {
    throw new Error(`AI field target ${target.fieldId} not found`);
  }

  switch (target.scope) {
    case 'field':
      return;
    case 'entry':
      if (!database.entriesById[target.entryId]) {
        throw new Error(`AI field entry target ${target.entryId} not found`);
      }
      return;
    case 'entries':
      for (const entryId of target.entryIds) {
        if (!database.entriesById[entryId]) {
          throw new Error(`AI field entry target ${entryId} not found`);
        }
      }
      return;
    default: {
      const unhandledTarget: never = target;
      throw new Error(`Unhandled AI field target: ${String(unhandledTarget)}`);
    }
  }
}

export async function runDatabaseAiField(
  options: RunDatabaseAiFieldOptions
): Promise<void> {
  const {
    contentId,
    locale,
    prompt,
    target,
    traceContext,
    generationWriteContext,
    abortSignal
  } = options;
  const streamState = DatabaseStreamState.getInstance();
  if (streamState.isStreamingActive) {
    throw new Error('Another database stream is already active');
  }

  assertTargetExists(contentId, target);

  const entryScope = buildDatabaseEntryScope(options);
  const batches = readDatabaseContentBatches(contentId, {
    batchSize: DATABASE_AI_FIELD_BATCH_SIZE,
    includeAiFields: true,
    includeSelection: false,
    ...(options.entryFieldValuesSnapshot !== undefined && {
      entryFieldValuesSnapshot: options.entryFieldValuesSnapshot
    }),
    entryScope
  });
  const targetEntryIds =
    batches.length > 1
      ? batches.flatMap(({ rowIdToEntryIdMappings }) => [
          ...rowIdToEntryIdMappings.values()
        ])
      : undefined;

  let reader: Awaited<ReturnType<typeof fetchDatabaseAiTeammate>> | undefined;
  let readerCancellation: Promise<void> | undefined;
  let ownsLocalStream = false;
  let streamCompletionScheduled = false;
  const cancelReader = (): Promise<void> | undefined => {
    if (!reader || readerCancellation) {
      return readerCancellation;
    }

    readerCancellation = reader.cancel().catch((cancelError) => {
      logError(
        'Failed to cancel database AI field stream',
        cancelError instanceof Error ? cancelError : new Error(String(cancelError))
      );
    });
    return readerCancellation;
  };
  const abortRun = (): void => {
    if (ownsLocalStream && streamState.isAiFieldStreamingActive) {
      streamState.abortStreaming();
    }
    void cancelReader();
  };

  abortSignal?.addEventListener('abort', abortRun);

  try {
    abortSignal?.throwIfAborted();
    streamState.clearErrors();
    streamState.startAiFieldStreaming(contentId, target, {
      rowIdToEntryIdMappings: batches[0].rowIdToEntryIdMappings,
      ...(generationWriteContext !== undefined && { generationWriteContext }),
      ...(targetEntryIds !== undefined && { targetEntryIds })
    });
    ownsLocalStream = true;
    abortSignal?.throwIfAborted();

    for (const [batchIndex, { content }] of batches.entries()) {
      reader = await fetchDatabaseAiTeammate({
        input: {
          prompt,
          locale,
          databaseContext: {
            content_id: contentId,
            title: content.title,
            url: content.url,
            csv: content.csv
          }
        },
        traceContext,
        abortSignal
      });
      abortSignal?.throwIfAborted();

      for await (const documentPart of iterateDatabaseAiTeammateDocumentParts(reader)) {
        abortSignal?.throwIfAborted();
        streamState.processChunk(documentPart);
      }
      abortSignal?.throwIfAborted();

      const nextBatch = batches[batchIndex + 1];
      if (nextBatch) {
        await streamState.advanceAiFieldBatch(
          contentId,
          nextBatch.rowIdToEntryIdMappings
        );
        abortSignal?.throwIfAborted();
        reader = undefined;
      } else {
        await streamState.completeStreaming();
        streamCompletionScheduled = true;
        abortSignal?.throwIfAborted();
        const errors = streamState.getErrors();
        if (errors.length > 0) {
          throw new Error(errors.map(({ message }) => message).join('; '));
        }
      }
    }

    streamState.markAiFieldRunCompleted();
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error) {
    // If completion has not scheduled its delayed reset, abort only this AI-field stream.
    if (!streamCompletionScheduled && streamState.isAiFieldStreamingActive) {
      streamState.abortStreaming();
    }

    await cancelReader();

    throw abortSignal?.aborted && isAbortError(error) ? abortSignal.reason : error;
  } finally {
    abortSignal?.removeEventListener('abort', abortRun);
  }
}
